import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Send, Phone, MessageSquareText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES, TIME_SLOTS, PHONE_TEL, PHONE_DISPLAY } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
// The component talks to the adapter layer, not to a backend. It does not know
// or care which implementation answers — see lib/backend/index.js. The 18s
// timeout that used to live here now lives in the contract, shared by every
// adapter so the visitor's experience does not change with the backend.
import { submitBooking } from "@/lib/backend";

const SMS_NUMBER = "+17203295714";

/**
 * Minimum time the sending state stays on screen, in ms.
 *
 * The request is usually sub-second, so without a floor the shimmer appears and
 * vanishes inside a single blink. That does not read as "sent" — it reads as a
 * glitch, which is worse than no animation at all.
 *
 * THIS DOES NOT DELAY THE SUBMISSION. The request is fired before the clock
 * starts and is already finished by the time anything waits; only the moment
 * the UI is allowed to change is held back. A slow request is never made
 * slower — if it took longer than this, nothing waits at all.
 */
const MIN_SENDING_MS = 400;

// Touch detection for the "send as text" accelerator on the success panel.
//
// WHY A POINTER QUERY AND NOT A WIDTH BREAKPOINT: this is a question about the
// input device, not the viewport. `sms:` links do nothing on most desktop
// browsers, so offering the button there is a dead end — but a narrow desktop
// window is still a desktop, and a 1024px tablet is still a phone-like device
// that can send a text. `(hover: none) and (pointer: coarse)` asks the real
// question: is the primary input a finger rather than a mouse?
//
// Evaluated once via a lazy initialiser rather than in an effect, so the button
// never flashes in and out on first paint. The trade-off: a 2-in-1 that switches
// between laptop and tablet mode mid-session will not re-evaluate until reload.
// That is an acceptable miss for a one-time confirmation panel.
const detectTouch = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: none) and (pointer: coarse)").matches;

const fieldCls =
  "h-12 rounded-none bg-[#050706] border-white/15 text-[#f0f4f2] placeholder:text-[#8a9690]/60 focus-visible:ring-[#c5a059] focus-visible:ring-1 focus-visible:ring-offset-0";

export const Booking = () => {
  const { t } = useLang();
  const b = t.booking;
  // `company` is the honeypot — see the hidden field at the end of the form.
  // A real visitor never fills it; an automated form-filler usually does.
  // The state key mirrors the WIRE key, not the DOM one: the input is id/name
  // `ref_code` so browser autofill has no `organization` mapping to latch onto.
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", time: "", notes: "", company: "" });
  const [date, setDate] = useState(null);
  /**
   * The date popover's open state, lifted out of Radix.
   *
   * WHY THIS HAS TO EXIST AT ALL — see ELITE_SPA_FIX_8.md. The short version:
   * the time field is ONE component (Radix Select), so selecting an item sets
   * the value and closes the list in the same handler, inside the library. The
   * date field is TWO independent components stacked — a Radix Popover that
   * knows nothing about dates, wrapping a react-day-picker Calendar that knows
   * nothing about popovers. Left uncontrolled, the Popover only closes on an
   * outside click, Escape, or a second click on the trigger; a day button
   * inside its own portalled content is an INSIDE click, so picking a date
   * dismissed nothing. This state is the missing wire between them.
   */
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [isTouch] = useState(detectTouch);
  // Live: framer-motion watches the media query, so a visitor who changes the
  // OS setting is respected without reloading the page.
  const reduceMotion = useReducedMotion();

  // The form's measured height, captured at the moment of a successful submit
  // and held on the column so the section cannot collapse. See the block
  // comment on the column div below.
  const formRef = useRef(null);
  const [reservedHeight, setReservedHeight] = useState(null);

  /**
   * Bring the confirmation into view when it mounts.
   *
   * A CALLBACK REF RATHER THAN AN EFFECT, and the reason is specific to this
   * component: <AnimatePresence mode="wait"> does NOT mount the incoming child
   * on the render where `submitted` becomes truthy. It keeps the outgoing form
   * mounted until its exit finishes, and only then mounts the panel. An effect
   * keyed on `submitted` would therefore run while successRef.current is still
   * null — which is exactly the kind of thing you then "fix" with a setTimeout
   * and a guessed delay.
   *
   * A callback ref fires during the commit in which the node actually enters
   * the DOM, whenever that turns out to be. NO TIMEOUT IS NEEDED ANYWHERE, and
   * nothing has to know how long the exit animation takes.
   *
   * scrollIntoView forces a synchronous layout, so the geometry it reads is the
   * post-mount one, including the reserved height applied in the same render.
   */
  const scrollSuccessIntoView = useCallback(
    (node) => {
      if (!node) return; // detach — nothing to do

      // block: "center" is what was asked for, and is right whenever the panel
      // fits. When it does NOT fit — a small phone, or the longer Spanish and
      // Chinese copy — centring pushes the title off the top of the screen and
      // the visitor lands mid-panel, which is the very failure this is meant to
      // fix. So a panel taller than the viewport is aligned to its start
      // instead, which shows the tick and "Request sent." first.
      const fits = node.getBoundingClientRect().height <= window.innerHeight;

      node.scrollIntoView({
        // TASK 3. Smooth scrolling is motion like any other. Under
        // prefers-reduced-motion the visitor still ends up looking at the
        // panel — they just arrive there instantly.
        behavior: reduceMotion ? "auto" : "smooth",
        block: fits ? "center" : "start",
      });
    },
    [reduceMotion]
  );

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    // Email is deliberately NOT in this check — it is optional and must never
    // block a submission.
    if (!form.name || !form.phone || !form.service || !date || !form.time) {
      toast.error(b.missing);
      return;
    }
    setSubmitting(true);
    const startedAt = Date.now();
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      service: form.service,
      date: format(date, "yyyy-MM-dd"),
      time: form.time,
      notes: form.notes,
      // Honeypot. Must be passed through explicitly: the Apps Script adapter
      // reads `data.company`, and buildPayload() in the contract would drop it.
      // Empty for a real visitor, so it changes nothing for them.
      company: form.company,
    };

    // One call, one result shape, whichever backend is configured. The adapter
    // resolves rather than throwing for expected failures — but `ok` must still
    // be checked.
    //
    // The .catch() is not defensive noise. If the adapter ever DID throw, this
    // function would unwind past setSubmitting(false) and the button would sit
    // disabled and shimmering forever, on a form the visitor can no longer use.
    // An animation that outlives its request is exactly what Fix Pass 6 set out
    // to avoid, so a throw is normalised into an ordinary failed result and
    // every line below runs on every path.
    const result = await submitBooking(payload).catch((thrown) => ({
      ok: false,
      error: `threw: ${thrown && thrown.message ? thrown.message : thrown}`,
    }));

    // Hold the sending state to its floor. This sits AFTER the request and
    // BEFORE the UI changes, which is the only place it can go: on success the
    // form unmounts and takes the button with it, so holding it any later would
    // hold nothing.
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_SENDING_MS) {
      await new Promise((resolve) => setTimeout(resolve, MIN_SENDING_MS - elapsed));
    }

    if (result.ok) {
      // Measured HERE, while the form is still mounted and at its natural
      // height. offsetHeight is the border-box height, and the column has no
      // padding or border of its own, so it transfers across directly.
      if (formRef.current) {
        setReservedHeight(formRef.current.offsetHeight);
      }
      setSubmitted({ ...payload, date: format(date, "MMM d, yyyy"), time: form.time });
      setForm({ name: "", phone: "", email: "", service: "", time: "", notes: "", company: "" });
      setDate(null);
    } else {
      // Same toast as before, for every failure mode. `result.error` is a
      // machine code for the console, never shown to the visitor.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[booking] submission failed:", result.error);
      }
      toast.error(`${b.fail} ${PHONE_DISPLAY}`);
    }
    setSubmitting(false);
  };

  const smsHref = submitted
    ? `sms:${SMS_NUMBER}?&body=${encodeURIComponent(b.smsBody({ name: submitted.name, service: submitted.service, date: submitted.date, time: submitted.time, notes: submitted.notes, email: submitted.email }))}`
    : "#";

  return (
    <section id="book" data-testid="booking-section" className="relative py-24 lg:py-40 bg-[#0c100e] overflow-hidden">
      <span aria-hidden="true" className="ghost-char bottom-6 left-2">約</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{b.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
            {b.title.pre}<span className="italic text-[#c5a059]">{b.title.it}</span>{b.title.post}
          </h2>
          <p className="mt-8 text-[#8a9690] font-light leading-relaxed max-w-md">{b.body}</p>
          <div className="mt-10 border-t border-white/10 pt-8">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8a9690]">{b.preferTalk}</span>
            <a
              href={PHONE_TEL}
              data-testid="booking-call-instead"
              className="mt-3 flex items-center gap-3 font-serif-display text-2xl text-[#c5a059] hover:text-[#dcc286] transition-colors duration-300"
            >
              <Phone size={18} /> {PHONE_DISPLAY}
            </a>
          </div>
        </motion.div>

        {/* -------------------------------------------------------------------
            RESERVED HEIGHT (Fix Pass 7)

            The success panel is substantially shorter than the form — roughly
            320px on a 375px viewport. Without this, swapping one for the other
            collapses the column, the section shrinks by that much, and every
            section below it jumps up by the same amount at the exact instant
            the visitor is trying to read their confirmation. The wrong scroll
            position was the symptom; this is the cause.

            MEASURED, NOT HARDCODED. A fixed `min-h-[845px]` would be wrong
            almost everywhere: the form's height varies with the viewport (the
            name/phone and date/time pairs are stacked below 640px and side by
            side above it), and with the language — the EN, 中文 and ES labels
            and placeholders all wrap differently. The real height is taken from
            the form itself, once, just before it is swapped out.

            justify-center distributes the freed space above and below the panel
            rather than dumping all of it underneath, where it would read as a
            broken page. NEITHER STATE'S DESIGN CHANGES: the panel keeps its
            border, padding, width and content exactly, and the flex classes are
            applied only while a height is reserved — which is only ever in the
            success state, since the form IS the measurement and can never be
            shorter than it.
            ------------------------------------------------------------------- */}
        <div
          className={`lg:col-span-7${reservedHeight ? " flex flex-col justify-center" : ""}`}
          style={reservedHeight ? { minHeight: reservedHeight } : undefined}
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                ref={scrollSuccessIntoView}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="booking-success-panel"
                className="border border-[#c5a059]/40 bg-[#050706]/60 backdrop-blur-sm p-10 lg:p-14 text-center"
              >
                <CheckCircle2 size={40} className="mx-auto text-[#c5a059]" />
                <h3 className="mt-6 font-serif-display text-3xl lg:text-4xl text-[#f0f4f2]">{b.successTitle}</h3>
                <p className="mt-4 text-[#8a9690] font-light max-w-md mx-auto leading-relaxed">{b.successText}</p>

                {/* The text message is now an ACCELERATOR, not the delivery
                    mechanism — the submission above already reached the backend.
                    Shown only on touch devices: an sms: link does nothing on most
                    desktop browsers, so on a laptop this would be a dead end.
                    Desktop visitors still get the confirmation and the phone
                    number below. */}
                {isTouch && (
                  <>
                    <p className="mt-8 text-[#8a9690] font-light max-w-md mx-auto leading-relaxed text-sm">
                      {b.successNudge}
                    </p>
                    {/* SECONDARY styling: the outlined treatment already used by
                        the "Call Instead" button this replaces, and by the maps
                        buttons in Contact.jsx. No new colour, font or effect. */}
                    <a
                      href={smsHref}
                      data-testid="booking-sms-button"
                      className="mt-5 inline-flex items-center justify-center gap-3 border border-white/25 text-[#f0f4f2] hover:border-[#c5a059] hover:text-[#c5a059] px-8 py-4 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
                    >
                      <MessageSquareText size={14} /> {b.smsBtn}
                    </a>
                  </>
                )}

                {/* Phone stays visible on every device as the fallback. */}
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-[#8a9690] font-light">
                  <span>{b.callPrefix}</span>
                  <a
                    href={PHONE_TEL}
                    data-testid="booking-success-call"
                    className="text-[#c5a059] hover:text-[#dcc286] transition-colors duration-300"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
                <button
                  // Release the reservation as well, so a rotated or resized
                  // phone does not hold a stale height under a fresh form.
                  // Harmless either way — the form is never shorter than the
                  // height measured from it — but a stale number should not
                  // outlive the state it was taken for.
                  onClick={() => {
                    setSubmitted(null);
                    setReservedHeight(null);
                  }}
                  data-testid="booking-another-button"
                  className="mt-8 text-xs text-[#8a9690] hover:text-[#c5a059] tracking-[0.15em] uppercase transition-colors duration-300"
                >
                  {b.another}
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                ref={formRef}
                onSubmit={submit}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="border border-white/10 bg-[#050706]/60 backdrop-blur-sm p-8 lg:p-12 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.name}</label>
                    <Input data-testid="booking-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder={b.namePh} className={fieldCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.phone}</label>
                    <Input data-testid="booking-phone-input" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder={b.phonePh} type="tel" className={fieldCls} />
                  </div>
                </div>

                {/* Email — OPTIONAL. Placed directly after the name/phone pair
                    because it belongs with the other "how to reach you" fields
                    rather than among the appointment details below. It sits full
                    width on its own row instead of pairing with phone: an email
                    address is the longest value on the form, and at 375px a
                    half-width field would truncate what the visitor typed.
                    Same Input, same fieldCls, same label treatment — nothing
                    bespoke. */}
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.email}</label>
                  <Input
                    data-testid="booking-email-input"
                    value={form.email}
                    onChange={(e) => set("email")(e.target.value)}
                    placeholder={b.emailPh}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className={fieldCls}
                  />
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.service}</label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="booking-service-select" className={`${fieldCls} w-full`}>
                      <SelectValue placeholder={b.servicePh} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0c100e] border-white/15 text-[#f0f4f2] rounded-none">
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={t.services.items[s.id].name} data-testid={`booking-service-option-${s.id}`}>
                          {t.services.items[s.id].name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.date}</label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="booking-date-picker"
                          className={`${fieldCls} w-full flex items-center justify-between border px-3 text-sm ${date ? "text-[#f0f4f2]" : "text-[#8a9690]/60"}`}
                        >
                          {date ? format(date, "MMMM d, yyyy") : b.datePh}
                          <CalendarIcon size={15} className="text-[#c5a059]" />
                        </button>
                      </PopoverTrigger>
                      {/* max-height MATCHES THE PATTERN SelectContent ALREADY USES.
                          shadcn's SelectContent ships with
                            max-h-[--radix-select-content-available-height] overflow-y-auto overflow-x-hidden
                          and PopoverContent ships with no max-height at all — so
                          the time list coped with being taller than the space
                          available and the calendar did not. The calendar is
                          ~316px tall and a phone in landscape has roughly
                          300-340px of usable viewport, so the last week's row
                          could be clipped and unreachable.

                          Radix sets --radix-popover-content-available-height to
                          the space on whichever side it chose AFTER collision
                          handling, so this adapts to a flip rather than assuming
                          the popover opens downward.

                          NO EFFECT WHENEVER THE CALENDAR FITS: max-height above
                          the content height does not clamp, and overflow-y-auto
                          shows no scrollbar without overflow. In portrait there
                          is always more room than 316px, so nothing changes.
                          overflow-x-hidden is inert here — w-auto shrink-wraps
                          the grid so there is nothing to overflow sideways — and
                          is carried over only to match the Select exactly. */}
                      <PopoverContent
                        className="w-auto p-0 bg-[#0c100e] border-white/15 rounded-none max-h-[--radix-popover-content-available-height] overflow-y-auto overflow-x-hidden"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={date}
                          // `required` makes the selection UN-CLEARABLE, which
                          // is what the Select next door already does — you
                          // cannot un-pick a time either.
                          //
                          // Without it, clicking the already-selected day called
                          // onSelect(undefined) and silently emptied a required
                          // field. It gates exactly one branch in the library:
                          //
                          //   if (activeModifiers.selected && !required) {
                          //     onSelect(undefined, day, …); return;   // deselect
                          //   }
                          //   onSelect(day, day, …);
                          //
                          // so with it set, that early return is unreachable and
                          // `d` is always a Date. Nothing else about the picker
                          // changes — see ELITE_SPA_FIX_9.md.
                          required
                          // Close on selection, matching the time Select.
                          //
                          // The `if (d)` cannot currently be false: `required`
                          // above removes the only path that passes undefined.
                          // It stays because react-day-picker still DECLARES
                          // this parameter as `Date | undefined`, and because
                          // the two props are otherwise silently coupled —
                          // dropping `required` later would reintroduce the
                          // empty-field bug with nothing to catch it.
                          onSelect={(d) => {
                            setDate(d);
                            if (d) setDateOpen(false);
                          }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          // Keyboard: without this, opening the popover lands
                          // focus on the previous-month chevron and the visitor
                          // has to Tab past the nav to reach the grid. With it,
                          // react-day-picker focuses the selected day (or
                          // today) so the arrow keys work immediately.
                          initialFocus
                          className="text-[#f0f4f2]"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.time}</label>
                    <Select value={form.time} onValueChange={set("time")}>
                      <SelectTrigger data-testid="booking-time-select" className={`${fieldCls} w-full`}>
                        <SelectValue placeholder={b.timePh} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0c100e] border-white/15 text-[#f0f4f2] rounded-none max-h-64">
                        {TIME_SLOTS.map((slot) => (
                          <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8a9690] mb-2">{b.notes}</label>
                  <Textarea
                    data-testid="booking-notes-input"
                    value={form.notes}
                    onChange={(e) => set("notes")(e.target.value)}
                    placeholder={b.notesPh}
                    rows={4}
                    className={`${fieldCls} h-auto resize-none`}
                  />
                </div>

                {/* -----------------------------------------------------------
                    SENDING STATE (Fix Pass 6)

                    Two effects, and the important property of both is that they
                    CANNOT OUTLIVE THE REQUEST. An animation still running after
                    the response has landed makes a working submission look
                    broken, which is worse than a static label.

                    Neither one is stopped by a cleanup call that a code path
                    could skip. Both are a pure function of `submitting`:

                      * the sweep is CONDITIONALLY RENDERED, so when submitting
                        goes false React unmounts the node and the CSS animation
                        ceases to exist along with it. There is nothing to
                        forget to stop.

                      * the icon's `animate` target is the resting state
                        {y:0, x:0, opacity:1} with a finite, non-repeating
                        transition. Framer interrupts the loop and travels there
                        from wherever it currently is, so it cannot be stranded
                        mid-drift or left invisible.

                    And `submitting` itself reaches false on every path —
                    success, failure and a thrown adapter — because submit()
                    normalises a throw into a result. Three layers, no cleanup.

                    Neither effect changes the button's size or position: the
                    sweep is absolutely positioned and the icon moves by
                    transform, which does not participate in layout.
                    ----------------------------------------------------------- */}
                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                  data-testid="booking-submit-button"
                  className="group relative overflow-hidden w-full bg-[#c5a059] text-[#050706] py-4 text-xs tracking-[0.25em] uppercase font-medium disabled:opacity-60 transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    {/* The message lifts, drifts away and fades out, then
                        returns at rest while invisible — so the loop never
                        shows the icon flying backwards. inline-flex so the
                        wrapper hugs the 14px icon exactly and the flex gap is
                        unchanged. */}
                    <motion.span
                      className="inline-flex"
                      animate={
                        submitting && !reduceMotion
                          ? { y: [0, -6, -11, 0, 0], x: [0, 4, 9, 0, 0], opacity: [1, 1, 0, 0, 1] }
                          : { y: 0, x: 0, opacity: 1 }
                      }
                      transition={
                        submitting && !reduceMotion
                          ? {
                              duration: 1.6,
                              times: [0, 0.45, 0.72, 0.73, 1],
                              ease: "easeInOut",
                              repeat: Infinity,
                            }
                          // The settle. Finite, no repeat, and the project's
                          // own curve — the same one the rest of this section
                          // animates on.
                          : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
                      }
                    >
                      <Send size={14} />
                    </motion.span>
                    {submitting ? b.sending : b.submit}
                  </span>
                  <span className="absolute inset-0 bg-[#dcc286] translate-y-full group-hover:translate-y-0 transition-transform duration-400" />
                  {submitting && (
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 ${reduceMotion ? "gold-sweep-static" : "gold-sweep"}`}
                    />
                  )}
                </button>

                {/* ---------------------------------------------------------------
                    HONEYPOT — invisible to people, attractive to form-filling bots.
                    The backend silently accepts and discards anything with this set.

                    HIDING TECHNIQUE: moved off-screen, NOT `display: none`.
                    `display:none` and `visibility:hidden` are the two things a bot
                    checks first, so a field hidden that way is the easiest kind to
                    skip. An off-screen element still computes as displayed and
                    visible, so a bot reading the DOM has no cheap tell.

                    aria-hidden="true" is what keeps it away from screen readers.
                    Off-screen positioning ALONE is the standard `sr-only` pattern —
                    it hides from sighted users while deliberately KEEPING content in
                    the accessibility tree, which is the opposite of what a honeypot
                    wants. Without aria-hidden, a screen-reader user would meet an
                    unlabelled "Reference code" box, have no way to know it must stay
                    empty, and lose their booking by filling it in.

                    tabIndex={-1} keeps it out of the keyboard tab order, so it is
                    unreachable even by someone navigating without a mouse — and it is
                    also what makes aria-hidden legitimate here rather than an ARIA
                    violation, since the subtree contains nothing tabbable.

                    autoComplete="off" is the one that protects real visitors: a
                    password manager or browser autofill filling this in would discard
                    a genuine booking with no error anywhere.

                    WHY id/name ARE `ref_code` AND NOT `company`. Chrome does not
                    reliably honour autoComplete="off" — when its heuristics read a
                    form as an address form (name + phone + email looks exactly like
                    one) it can autofill from the saved profile regardless. Those
                    heuristics key off the id, the name AND the label text, and
                    `company` is precisely the token that maps to `organization`.
                    `ref_code` has no such mapping, so there is nothing to match.

                    The DOM name is IRRELEVANT to what gets sent. This is a controlled
                    component: the value travels through React state, and the JSON key
                    is set in appsScript.js. The wire key is still `company`, which is
                    what the backend HONEYPOT_FIELD expects — renaming here needs no
                    backend change. That is also why the state key below stays
                    `form.company`: it mirrors the wire, not the DOM.

                    PLACED LAST on purpose. The form is `space-y-6`, which adds
                    margin-top to every child after the first — inserting this at the
                    top would have pushed the whole form down 24px, a visible design
                    change. Last, and absolutely positioned, it is out of flow and
                    changes nothing.
                    --------------------------------------------------------------- */}
                <div
                  aria-hidden="true"
                  className="absolute -left-[9999px] top-0 h-px w-px overflow-hidden"
                >
                  <label htmlFor="ref_code">Reference code</label>
                  <input
                    id="ref_code"
                    name="ref_code"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company}
                    onChange={(e) => set("company")(e.target.value)}
                  />
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
